import { Dot } from "@components"
import { Badge, Button, Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger } from "@heroui/react"
import { AdjustmentsHorizontalIcon, ChevronDownIcon } from "@heroicons/react/20/solid"
import PropTypes from "prop-types"

const FilterSelector = ({
  closeOnSelect = false, 
  color = "default",
  disallowEmptySelection = true,
  filters, 
  icon,
  selectedFilters, 
  selectionMode = "multiple",
  showFilterCount = false,
  toggleFilter,
  variant = "flat",
}) => {
  const filterIcon = icon || <AdjustmentsHorizontalIcon className="w-auto h-5" />
  
  const getItems = filters => {
    const hasSections = Array.isArray(filters)
    
    if (hasSections) {
      return filters.map(({ section, items }) => (
        <DropdownSection key={section} title={section}>
          {Object.entries(items).map(([key, { name, dotColor }]) => (
            <DropdownItem key={key} className="capitalize">
              <div className="flex items-center">
                {dotColor && <Dot color={dotColor} />}
                {name}
              </div>
            </DropdownItem>
          ))}
        </DropdownSection>
      ))
    }

    return Object.keys(filters).map(key => (
      <DropdownItem key={key} className="capitalize">
        <div className="flex items-center">
          {filters[key]?.icon && (filters[key].icon)}
          {filters[key]?.dotColor && <Dot color={filters[key]?.dotColor} />}
          {filters[key].name}
        </div>
      </DropdownItem>
    ))
  }

  const items = getItems(filters)

  return (
    <Badge className="font-semibold text-white" color="success" content={selectedFilters.length} size="md" showOutline={false} isInvisible={!showFilterCount || selectedFilters.length === 0}>
      <Dropdown>
        <DropdownTrigger className="flex-shrink-0 hidden sm:flex"> 
          <Button
            startContent={filterIcon}
            endContent={<ChevronDownIcon className="w-auto h-5 outline-none" />}
            variant={variant}
            color={color}
          />
        </DropdownTrigger>

        <DropdownMenu
          disallowEmptySelection={disallowEmptySelection}
          aria-label="Filters"
          closeOnSelect={closeOnSelect}
          selectedKeys={selectedFilters}
          selectionMode={selectionMode}
          onSelectionChange={toggleFilter}
        >
          {items}
        </DropdownMenu>
      </Dropdown>
    </Badge>
  )
}

FilterSelector.propTypes = {
  caption: PropTypes.string,
  closeOnSelect: PropTypes.bool,
  color: PropTypes.string,
  disallowEmptySelection: PropTypes.bool,
  filters: PropTypes.oneOfType([
    PropTypes.objectOf(
      PropTypes.shape({
        icon: PropTypes.node,
        name: PropTypes.string.isRequired,
        dotColor: PropTypes.string,
      })
    ),
    PropTypes.arrayOf(
      PropTypes.shape({
        section: PropTypes.string.isRequired,
        items: PropTypes.objectOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            dotColor: PropTypes.string,
          })
        ).isRequired,
      })
    ),
  ]).isRequired,  
  selectedFilters: PropTypes.array.isRequired,
  selectionMode: PropTypes.string,
  showFilterCount: PropTypes.bool,
  toggleFilter: PropTypes.func.isRequired,
  variant: PropTypes.string,
}

export default FilterSelector
